import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { XMarkIcon, ArrowDownTrayIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../Spinner';

const STATUS_CONFIG = {
  new:         { label: 'New',         color: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'In Progress', color: 'bg-indigo-100 text-indigo-800' },
  follow_up:   { label: 'Follow Up',   color: 'bg-orange-100 text-orange-800' },
  converted:   { label: 'Converted',   color: 'bg-green-100 text-green-800' },
  lost:        { label: 'Lost',        color: 'bg-red-100 text-red-800' },
};

// Exports the currently-loaded drill-down leads (client-side, no extra API call)
const downloadLeads = (leads, title) => {
  if (!leads || leads.length === 0) { toast.error('No leads to download'); return; }

  const headers = ['Name', 'Phone', 'Email', 'City', 'Brand', 'Model', 'Status', 'Assigned To', 'Created', 'Last Remark'];
  const rows = leads.map(l => [
    l.customerInfo?.name || '',
    l.customerInfo?.phone || '',
    l.customerInfo?.email || '',
    l.customerInfo?.city || '',
    l.leadInfo?.brand || '',
    l.leadInfo?.model || '',
    STATUS_CONFIG[l.status]?.label || l.status || '',
    l.assignment?.assignedTo ? `${l.assignment.assignedTo.firstName} ${l.assignment.assignedTo.lastName}` : '',
    l.createdAt ? format(new Date(l.createdAt), 'dd-MM-yyyy HH:mm') : '',
    l.lastRemark || '',
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Leads');
  const safeName = (title || 'leads').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  XLSX.writeFile(wb, `${safeName}-${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
  toast.success(`Downloaded ${leads.length} lead${leads.length !== 1 ? 's' : ''}`);
};

// Debounced search box — calls onSearchChange 350ms after typing stops, so
// each keystroke doesn't trigger a fresh drill-down fetch.
const SearchBox = ({ value, onChange }) => {
  const [local, setLocal] = useState(value || '');
  useEffect(() => { setLocal(value || ''); }, [value]);
  useEffect(() => {
    const t = setTimeout(() => { if (local !== (value || '')) onChange(local); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);
  return (
    <div className="relative">
      <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
      <input
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder="Search name or phone…"
        className="input text-xs !py-1.5 !pl-8 w-48"
      />
    </div>
  );
};

// Inline expandable panel — shows the matching leads without leaving the
// report page. Renders as a table row (asRow, default) to sit directly under
// the clicked row, or as a plain block (asRow=false) below non-table triggers
// like summary cards.
const PanelBody = ({ title, leads, isLoading, total, onClose, search, onSearchChange, hasMore, onLoadMore, loadingMore }) => (
      <div className="px-4 py-4">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <p className="text-sm font-semibold text-gray-700">
            {title}
            {typeof total === 'number' && <span className="ml-2 text-gray-400 font-normal">({total} lead{total !== 1 ? 's' : ''})</span>}
          </p>
          <div className="flex items-center gap-2">
            {onSearchChange && <SearchBox value={search} onChange={onSearchChange} />}
            {leads && leads.length > 0 && (
              <button
                onClick={() => downloadLeads(leads, title)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary-700 hover:text-primary-800 hover:bg-primary-50 rounded-lg transition-colors"
                title="Download these leads"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download
              </button>
            )}
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-200">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="py-8 text-center"><Spinner size="md" /></div>
        ) : !leads || leads.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No matching leads.</p>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Name & Phone</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Brand & Model</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Assigned To</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Created</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leads.map(l => {
                  const st = STATUS_CONFIG[l.status] || { label: l.status, color: 'bg-gray-100 text-gray-600' };
                  return (
                    <tr key={l._id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{l.customerInfo?.name || '—'}</div>
                        <div className="text-xs text-gray-500">{l.customerInfo?.phone || '—'}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {[l.leadInfo?.brand, l.leadInfo?.model].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${st.color}`}>{st.label}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {l.assignment?.assignedTo ? `${l.assignment.assignedTo.firstName} ${l.assignment.assignedTo.lastName}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {l.createdAt ? format(new Date(l.createdAt), "dd MMM ''yy") : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/leads/${l._id}`} className="text-primary-600 hover:underline text-xs font-medium">View</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(hasMore || (typeof total === 'number' && total > leads.length)) && (
              <div className="flex items-center justify-center gap-3 py-2 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  Showing {leads.length}{typeof total === 'number' ? ` of ${total}` : ''}.
                </p>
                {hasMore && onLoadMore && (
                  <button
                    onClick={onLoadMore}
                    disabled={loadingMore}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline disabled:opacity-50"
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
);

export const DrillDownPanel = ({ colSpan, asRow = true, ...bodyProps }) => {
  const body = <PanelBody {...bodyProps} />;
  if (!asRow) return <div className="bg-gray-50 border border-gray-200 rounded-xl mb-4">{body}</div>;
  return (
    <tr>
      <td colSpan={colSpan} className="p-0 bg-gray-50 border-y border-gray-200">{body}</td>
    </tr>
  );
};
